import React, { useEffect, useMemo, useRef, useState } from "react";
import { searchAirports, formatAirport } from "@/lib/airports";

/* AirportInput — controlled text input with dropdown suggestions.
   On select, value becomes "City – IATA" (e.g. "Riyadh – RUH").
   Free-typing is also allowed — users can still enter custom text. */
export default function AirportInput({ value, onChange, placeholder, testId, disabled, className = "byf-inp" }) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);

  const suggestions = useMemo(() => searchAirports(value || "", 8), [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const choose = (a) => {
    onChange(formatAirport(a));
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(suggestions.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter") {
      if (highlight >= 0 && highlight < suggestions.length) { e.preventDefault(); choose(suggestions[highlight]); }
    } else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        autoComplete="off"
        spellCheck={false}
        className={className}
        value={value || ""}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testId}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlight(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && suggestions.length > 0 && (
        <ul
          role="listbox"
          data-testid={`${testId}-suggestions`}
          className="absolute z-30 left-0 right-0 mt-1 max-h-72 overflow-auto bg-white border border-[#e2e8f0] rounded-md shadow-lg"
        >
          {suggestions.map((a, i) => {
            const active = i === highlight;
            return (
              <li
                key={`${a.iata}-${i}`}
                role="option"
                aria-selected={active}
                data-testid={`${testId}-suggestion-${a.iata}`}
                onMouseDown={(e) => { e.preventDefault(); choose(a); }}
                onMouseEnter={() => setHighlight(i)}
                className={`px-3 py-2 cursor-pointer text-sm flex items-center justify-between gap-3 ${active ? "bg-[#eff6ff]" : "hover:bg-[#f8fafc]"}`}
              >
                <div className="min-w-0">
                  <div className="text-[#0f1b2d] font-medium truncate">{a.city}, <span className="text-[#475569] font-normal">{a.country}</span></div>
                  <div className="text-xs text-[#64748b] truncate">{a.name}</div>
                </div>
                <span className="text-xs font-mono font-semibold text-[#2563eb] bg-[#eff6ff] border border-[#bfdbfe] rounded px-1.5 py-0.5 flex-shrink-0">{a.iata}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
